import { useEffect, useState } from "react";
import { Megaphone, Send, Trash2, Loader2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { PageHeader, StatusPill } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { todayIST, fmtDate, fmtDateTime } from "@/lib/format";

const EMPTY = { festival: "", offer_text: "", areas: [], start_date: todayIST(), end_date: todayIST(3), banner: true, when: "now", send_at: "" };
const PILL = { queued: "pending", scheduled: "pending", sending: "pending", sent: "delivered" };

function CampaignForm({ areas, onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleArea = (a) => setF({ ...f, areas: f.areas.includes(a) ? f.areas.filter((x) => x !== a) : [...f.areas, a] });
  const submit = async () => {
    setBusy(true);
    try {
      const { when, ...body } = f;
      body.send_at = when === "later" && f.send_at ? new Date(f.send_at).toISOString() : null;
      const r = await api.post("/campaigns", body);
      toast.success(r.data.status === "scheduled" ? `Scheduled for ${fmtDateTime(r.data.send_at)}` : "Sending offer to customers now");
      setF(EMPTY); onCreated();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-3xl border border-[#E7E2D6] bg-white p-6 space-y-4" data-testid="campaign-form">
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1"><Label>Festival</Label><Input value={f.festival} onChange={set("festival")} placeholder="Diwali" data-testid="campaign-festival-input" /></div>
        <div className="space-y-1"><Label>Offer from</Label><Input type="date" value={f.start_date} onChange={set("start_date")} data-testid="campaign-start-input" /></div>
        <div className="space-y-1"><Label>Offer till</Label><Input type="date" value={f.end_date} onChange={set("end_date")} data-testid="campaign-end-input" /></div>
      </div>
      <div className="space-y-1"><Label>Offer text</Label><Textarea value={f.offer_text} onChange={set("offer_text")} maxLength={600} placeholder="Get 10% off on all batters from 20-24 Oct. Free Vada Batter 500 g on orders above ₹300!" data-testid="campaign-offer-input" /><div className="text-xs text-muted-foreground">Shown in the WhatsApp message: "Hi Ravi, {f.festival || "Diwali"} special from Nex Fresh! {f.offer_text || "…"} Order your fresh batter here: link"</div></div>
      <div className="space-y-2">
        <Label>Send to</Label>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setF({ ...f, areas: [] })} className={`rounded-full border px-3 py-1 text-xs font-semibold ${f.areas.length === 0 ? "border-[#0F382C] bg-[#0F382C] text-white" : "border-[#E7E2D6]"}`} data-testid="campaign-area-all">All active customers</button>
          {areas.map((a, i) => <button key={a} type="button" onClick={() => toggleArea(a)} className={`rounded-full border px-3 py-1 text-xs font-semibold ${f.areas.includes(a) ? "border-[#0F382C] bg-[#0F382C] text-white" : "border-[#E7E2D6]"}`} data-testid={`campaign-area-${i}`}>{a}</button>)}
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>When</Label>
          <div className="flex gap-2">
            {[["now", "Send now"], ["later", "Schedule"]].map(([k, l]) => <Button key={k} type="button" variant={f.when === k ? "default" : "outline"} className={f.when === k ? "bg-[#0F382C]" : ""} onClick={() => setF({ ...f, when: k })} data-testid={`campaign-when-${k}`}>{l}</Button>)}
          </div>
          {f.when === "later" && <Input type="datetime-local" value={f.send_at} onChange={set("send_at")} data-testid="campaign-send-at-input" />}
        </div>
        <label className="flex items-center justify-between gap-3 rounded-xl border border-[#E7E2D6] p-4">
          <div><div className="text-sm font-semibold">Show banner in customer portal</div><div className="text-xs text-muted-foreground">During the offer dates</div></div>
          <Switch checked={f.banner} onCheckedChange={(v) => setF({ ...f, banner: v })} data-testid="campaign-banner-toggle" />
        </label>
      </div>
      <Button disabled={busy || !f.festival.trim() || !f.offer_text.trim() || (f.when === "later" && !f.send_at)} onClick={submit} className="rounded-full bg-[#0F382C] hover:bg-[#134E3A]" data-testid="campaign-submit-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : f.when === "later" ? <><CalendarClock className="mr-2 h-4 w-4" />Schedule offer</> : <><Send className="mr-2 h-4 w-4" />Send offer now</>}
      </Button>
    </div>
  );
}

function CampaignRow({ c, reload }) {
  const act = (fn, msg) => fn().then(() => { toast.success(msg); reload(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="rounded-2xl border border-[#E7E2D6] bg-white p-4" data-testid={`campaign-${c.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-display font-semibold text-[#0F382C]">{c.festival} <span className="ml-2 text-xs font-normal text-muted-foreground">{fmtDate(c.start_date)} – {fmtDate(c.end_date)} · {c.areas.length ? c.areas.join(", ") : "All customers"}</span></div>
          <div className="mt-1 text-sm">{c.offer_text}</div>
          <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground" data-testid={`campaign-stats-${c.id}`}>
            <span>{c.status === "scheduled" ? `Scheduled ${fmtDateTime(c.send_at)}` : `Sent ${fmtDateTime(c.send_at)}`}</span>
            <span>Accepted {c.stats.accepted}</span><span>Delivered {c.stats.delivered}</span><span>Read {c.stats.read}</span><span className="text-rose-600">Failed {c.stats.failed}</span>
            {c.error && <span className="text-rose-600">{c.error}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={PILL[c.status]} label={c.status} />
          {c.status === "scheduled" && <Button size="sm" variant="outline" onClick={() => act(() => api.post(`/campaigns/${c.id}/send-now`), "Sending now")} data-testid={`campaign-send-now-${c.id}`}>Send now</Button>}
          {c.status !== "sending" && <Button size="icon" variant="ghost" onClick={() => act(() => api.delete(`/campaigns/${c.id}`), "Offer removed")} data-testid={`campaign-delete-${c.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
        </div>
      </div>
    </div>
  );
}

export default function Offers() {
  const [rows, setRows] = useState([]);
  const [areas, setAreas] = useState([]);
  const load = () => api.get("/campaigns").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setAreas([...new Set(r.data.filter((c) => c.active && c.area).map((c) => c.area))].sort()));
  }, []);
  return (
    <div data-testid="offers-page">
      <PageHeader eyebrow="Festival offers" title="WhatsApp offer campaigns" subtitle="Send a festival offer to all customers or chosen areas, now or at a scheduled time." />
      <div className="grid gap-6 lg:grid-cols-5">
        <div className="lg:col-span-3"><CampaignForm areas={areas} onCreated={load} /></div>
        <div className="lg:col-span-2 space-y-3" data-testid="campaign-list">
          <div className="flex items-center gap-2 font-display font-semibold text-[#0F382C]"><Megaphone className="h-5 w-5 text-[#D96B43]" />Campaigns</div>
          {rows.length === 0 && <div className="rounded-2xl border border-dashed border-[#E7E2D6] p-6 text-sm text-muted-foreground">No offers yet.</div>}
          {rows.map((c) => <CampaignRow key={c.id} c={c} reload={load} />)}
        </div>
      </div>
    </div>
  );
}
